import { getEnvVar } from '../../utils/env';
import {
  AgreementsResponse,
  PaymentMethodsResponse,
  Receipt,
  ReceiptHeader,
  StripeCheckoutResponse,
  StripeTVODRequest,
  StripeTVODResponse,
  VippsAgreementResponse,
  VippsCheckoutResponse,
} from './types';

const apiUrl = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.default-payment-provider.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1`;

const customerUrl = `${apiUrl}/customer`;
const receiptApiUrl = `${apiUrl}/receipt`;

export const checkCustomerExists = async (
  jwtToken: string,
): Promise<boolean> => {
  const response = await fetch(customerUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
  });
  return response.ok;
};

export const getCustomerPaymentMethods = async (
  jwtToken: string,
): Promise<PaymentMethodsResponse> => {
  try {
    const paymentMethodsResponse = await fetch(
      `${customerUrl}/paymentMethods`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
      },
    );

    if (!paymentMethodsResponse.ok) {
      if (paymentMethodsResponse.status === 404) {
        // Customer doesn't exist yet in Stripe, will be created during checkout
        console.log(
          'Customer not found in Stripe, returning empty payment methods',
        );
      } else {
        console.error(
          `Failed to get customer payment methods. Status: ${paymentMethodsResponse.status}`,
        );
      }

      return {
        paymentMethods: [],
        externalPaymentUserId: '',
      };
    }

    const response = await paymentMethodsResponse.json();
    return {
      ...response,
    } as PaymentMethodsResponse;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    return {
      paymentMethods: [],
      externalPaymentUserId: '',
    };
  }
};

/**
 * Creates a new Stripe subscription using the payment method, product payment ID, and Stripe product ID
 * This uses the new checkout flow to handle 3D Secure authentication if needed
 * @param paymentMethodId The ID of the payment method to use
 * @param productPaymentId The Vimond product payment ID
 * @param stripeProductId The Stripe product ID
 * @param jwtToken JWT token for authentication
 * @returns Response containing subscriptionId and clientSecret
 */
const stripeCheckoutUrl = `${apiUrl}/pay/stripe/checkout`;
export const createStripeSubscription = async (
  paymentMethodId: string,
  productPaymentId: string,
  stripeProductId: string,
  jwtToken: string,
  voucherCode?: string,
): Promise<StripeCheckoutResponse> => {
  const checkoutResponse = await fetch(stripeCheckoutUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({
      paymentMethodId,
      vimondProductPaymentId: productPaymentId,
      stripeProductId,
      voucherCode,
    }),
  });

  if (!checkoutResponse.ok) {
    console.error('Failed to create Stripe subscription');
    throw new Error('Failed to create subscription');
  }

  return (await checkoutResponse.json()) as StripeCheckoutResponse;
};

// Removed deprecated createSetupIntent function - use createStripeSubscription instead

export const updateCustomerDefaultPaymentMethod = async (
  paymentMethodId: string,
  jwtToken: string,
): Promise<void> => {
  const updateCustomerDefaultPaymentMethodResponse = await fetch(customerUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({
      defaultPaymentMethod: paymentMethodId,
    }),
  });

  if (!updateCustomerDefaultPaymentMethodResponse.ok) {
    throw new Error('Failed to update customer default payment method');
  }

  console.log('Successfully updated customer default payment method');
};

export const removePaymentMethod = async (
  paymentMethodId: string,
  jwtToken: string,
): Promise<void> => {
  const response = await fetch(
    `${customerUrl}/paymentMethods/${paymentMethodId}/remove`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to remove payment method');
  }

  console.log('Successfully removed payment method');
};

// Vipps payment functions
export const initiateVippsPayment = async (
  jwtToken: string,
  paymentData: {
    productId: string;
    productGroupId: string;
    productName: string;
    productPaymentId: string;
    price: number;
    intervalUnit: string;
    intervalCount: string;
    phoneNumber?: string;
    currency: string;
    voucherCode?: string;
  },
): Promise<VippsCheckoutResponse> => {
  const data = {
    ...paymentData,
    price: paymentData.price.toFixed(1),
  };
  const response = await fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.default-payment-provider.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/pay/vipps/checkout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to initiate Vipps payment');
  }

  return (await response.json()) as VippsCheckoutResponse;
};

export const checkVippsAgreementStatus = async (
  agreementId: string,
  jwtToken: string,
): Promise<VippsAgreementResponse> => {
  const response = await fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.default-payment-provider.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/pay/vipps/recurring/agreements/${agreementId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to check Vipps agreement status');
  }

  return (await response.json()) as VippsAgreementResponse;
};

/**
 * Get all subscriptions (agreements) for a user
 * @param jwtToken JWT token for authentication
 * @returns List of agreements for the user
 */
export const getAgreements = async (
  jwtToken: string,
): Promise<AgreementsResponse> => {
  const response = await fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.default-payment-provider.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/agreements`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
  );

  if (!response.ok) {
    console.error('Failed to fetch agreements');
    return [];
  }

  return (await response.json()) as AgreementsResponse;
};

/**
 * Cancel a subscription agreement
 * @param jwtToken JWT token for authentication
 * @param agreementId ID of the agreement to cancel
 * @returns Success status of the cancellation
 */
export const cancelAgreement = async (
  jwtToken: string,
  agreementId: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await fetch(
      `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.default-payment-provider.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/agreements/${agreementId}/stop`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
      },
    );

    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to cancel subscription',
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error cancelling agreement:', error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

// New functions for receipts / payment history
/**
 * Get all receipt headers for the current user
 * @param jwtToken JWT token for authentication
 * @returns List of receipt headers for the user
 */
export const getAllReceiptHeaders = async (
  jwtToken: string,
): Promise<ReceiptHeader[]> => {
  const response = await fetch(receiptApiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
  });

  if (!response.ok) {
    console.error('Failed to fetch receipt headers');
    // Consider throwing an error or returning a more specific error object
    throw new Error('Failed to fetch receipt headers');
  }

  // Return the basic receipt headers without fetching full details
  const headers = (await response.json()) as ReceiptHeader[];

  // Sort by date, newest first
  return headers.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};

/**
 * Get full details for a specific receipt
 * @param receiptId The ID of the receipt to fetch
 * @param jwtToken JWT token for authentication
 * @returns Full receipt details
 */
export const getReceiptDetails = async (
  receiptId: string,
  jwtToken: string,
  date?: string,
): Promise<Receipt> => {
  // If date is provided, create the full receipt path using date and receiptId (transactionId)
  const fullReceiptPath = date ? `${date}:${receiptId}` : receiptId;

  const response = await fetch(`${receiptApiUrl}/${fullReceiptPath}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
  });

  if (!response.ok) {
    console.error(`Failed to fetch receipt details for ID: ${receiptId}`);
    // Consider throwing an error or returning a more specific error object
    throw new Error(`Failed to fetch receipt details for ID: ${receiptId}`);
  }

  return (await response.json()) as Receipt;
};

/**
 * Helper function to properly capitalize payment provider names
 * @param provider The payment provider name (e.g., "stripe", "vipps")
 * @returns Properly capitalized provider name
 */
export const capitalizePaymentProvider = (
  provider?: string,
): string | undefined => {
  if (!provider) return undefined;

  // Common payment providers with proper capitalization
  const providers: Record<string, string> = {
    stripe: 'Stripe',
    vipps: 'Vipps',
    visa: 'VISA',
    mastercard: 'Mastercard',
    amex: 'American Express',
    'american express': 'American Express',
    paypal: 'PayPal',
    'apple pay': 'Apple Pay',
    'google pay': 'Google Pay',
  };

  const lowerProvider = provider.toLowerCase();
  return (
    providers[lowerProvider] ??
    // If not in our dictionary, capitalize first letter
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
};

/**
 * Helper function to properly capitalize payment method details
 * @param paymentMethod The payment method details (e.g., card info, phone number)
 * @returns Properly capitalized payment method details
 */
export const formatPaymentMethodDetails = (
  paymentMethod?: string,
): string | undefined => {
  if (!paymentMethod) return undefined;

  // Handle card brands with proper capitalization
  const brandMap: Record<string, string> = {
    visa: 'VISA',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    'american express': 'American Express',
  };

  // Convert the payment details to proper capitalization
  let formattedDetails = paymentMethod;
  Object.entries(brandMap).forEach(([search, replace]) => {
    const regex = new RegExp(search, 'i');
    if (regex.test(formattedDetails)) {
      formattedDetails = formattedDetails.replace(regex, replace);
    }
  });

  return formattedDetails;
};

/**
 * Create a TVOD (Transactional Video on Demand) using Stripe
 * @param data The TVOD request data
 * @param jwtToken JWT token for authentication
 * @returns Response containing TVOD details
 */
export const createStripeTVOD = async (
  data: StripeTVODRequest,
  jwtToken: string,
): Promise<StripeTVODResponse> => {
  const stripeTvodUrl = `${apiUrl}/pay/stripe/tvod`;
  const tvodResponse = await fetch(stripeTvodUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!tvodResponse.ok) {
    console.error('Failed to create Stripe TVOD');
    throw new Error('Failed to create TVOD');
  }

  return (await tvodResponse.json()) as StripeTVODResponse;
};
